import '@treasury/domain-components/layout/tm-list-layout';
import { WireFile } from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { css, html, PropertyValues } from 'lit';
import { columnBodyRenderer } from 'lit-vaadin-helpers';
import { customElement, property } from 'lit/decorators.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { classMap } from 'lit/directives/class-map.js';

export const tagName = 'wire-file-table';
@customElement(tagName)
export class WireFileTable extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Boolean })
    public isDesktop = false;

    @property({ type: Array })
    public wireFileActivity: WireFile[] = [];

    @property({ type: Array })
    public filteredWireFileActivity: WireFile[] = [];

    private get columns() {
        if (this.isDesktop) {
            return html`<vaadin-grid-sort-column
                    header="File"
                    path="fileName"
                    auto-width
                    flex-grow="0"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm font-medium text-action"
                                    >${wireFile.fileName}</span
                                >
                                <small class="text-xs text-tertiary"
                                    >Size: ${wireFile.fileSize}</small
                                >
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Uploaded"
                    path="uploadedBy"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.uploadedBy}</span>
                                <small class="text-xs text-tertiary">${wireFile.uploadDate}</small>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Processed"
                    path="processedBy"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.processedBy}</span>
                                <small class="text-xs text-tertiary"
                                    >${wireFile.processedDate}</small
                                >
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Wires"
                    path="amount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col justify-start">
                                <span class="text-sm font-medium"
                                    >${wireFile.amount.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}</span
                                >
                                <small class="text-xs text-tertiary"
                                    >Total wires: ${wireFile.totalWireCount}</small
                                >
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Approved/Submitted"
                    path="approvedCount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.approvedCount}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Pending"
                    path="pendingCount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.pendingCount}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Rejected"
                    path="rejectedCount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.rejectedCount}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Expired"
                    path="expiredCount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (wireFile: WireFile) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${wireFile.expiredCount}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>`;
        }
        return html` <vaadin-grid-column
                ${columnBodyRenderer(
                    (wireFile: WireFile) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm font-medium">${wireFile.fileName}</span>
                            <small class="text-xs text-tertiary">${wireFile.uploadedBy}</small>
                        </div>`,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                ${columnBodyRenderer(
                    (wireFile: WireFile) =>
                        html`<div class="flex flex-col items-end">
                            <span class="text-sm font-medium"
                                >${wireFile.amount.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                })}</span
                            >
                            <small class="text-xs text-tertiary">${wireFile.uploadDate}</small>
                        </div>`,
                    []
                )}
            ></vaadin-grid-column>`;
    }

    protected render() {
        return html`<tm-list-header
                .filterButton=${true}
                @filterButtonClick=${() => this.dispatchEvent(new CustomEvent('filter-wire-files'))}
                @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
            ></tm-list-header>
            ${this.renderTable()}`;
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('wireFileActivity')) {
            this.filteredWireFileActivity = this.wireFileActivity;
        }
    }

    private filterResults(e: CustomEvent) {
        this.filteredWireFileActivity = filterTableResults(e, this.wireFileActivity, [
            'amount',
            'fileName',
            'processedBy',
            'uploadedBy',
            'uploadDate',
        ]);
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.wireFileActivity.length) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                No wire files found
            </div>`;
        }
        return html` <vaadin-grid
            class=${classMap({
                'h-full': true,
                'shadow-md': !this.isDesktop,
            })}
            .items=${this.filteredWireFileActivity}
            @active-item-changed=${({ detail }: CustomEvent) => {
                if (!detail.value) {
                    return;
                }
                this.dispatchEvent(new CustomEvent('wire-file-select', { detail: detail.value }));
            }}
        >
            ${this.columns}
        </vaadin-grid>`;
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(body-cell) {
                    height: 100%;
                    padding: 8px 0;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireFileTable;
    }
}
